import React, { ReactNode, useEffect, useRef, useState } from "react";
import cs from "classnames";
import { Modal } from "reactstrap";

import { HostedPage } from "../../api/grpc/subscription/subscription";
import { Icon } from "../shared/Icon/Icon";
import { ProductStoreEmbeddedCheckout } from "./ProductStoreEmbeddedCheckout";

import "./ProductStoreCartDialog.scss";

interface Props {
  title: string;
  /** Under the title, in the review step. */
  subtitle?: string;
  /**
   * The step before Chargebee's checkout, e.g. the cart. Without it the
   * dialog opens straight in checkout and has no "Back to order".
   */
  review?: (onContinue: () => void) => ReactNode;
  /** Creates the Chargebee checkout; called for every new session. */
  createCheckout: () => Promise<Pick<HostedPage, "url">>;
  onClose: () => void;
  /** Payment went through; Chargebee's thank-you page is showing. */
  onComplete?: () => void;
}

/**
 * The Product Store's checkout dialog: an optional review step, then
 * Chargebee's checkout under the same header, then Chargebee's thank-you page.
 */
export const ProductStoreCheckoutDialog = ({
  title,
  subtitle,
  review,
  createCheckout,
  onClose,
  onComplete,
}: Props) => {
  const hasReview = !!review;
  const [view, setView] = useState<"review" | "checkout" | "complete">(
    hasReview ? "review" : "checkout"
  );
  const backRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const showReview = () => setView("review");

  // Move focus off controls that just disappeared: "Continue" with the review
  // step, "Back to order" once the order is placed.
  useEffect(() => {
    if (view === "checkout" && hasReview) {
      backRef.current?.focus();
    } else if (view === "complete") {
      closeRef.current?.focus();
    }
  }, [view, hasReview]);

  return (
    <Modal
      isOpen
      centered
      toggle={onClose}
      // In checkout, a click outside mustn't discard payment details.
      backdrop={view === "checkout" ? "static" : true}
      className={cs("ProductStoreCartDialog", {
        "ProductStoreCartDialog--checkout": view !== "review",
      })}
      labelledBy="product-store-checkout-title"
    >
      <div className="ProductStoreCartDialog__header">
        <div>
          <h2 id="product-store-checkout-title">
            {view === "complete" ? "Order placed" : title}
          </h2>
          {view === "review" && subtitle && <p>{subtitle}</p>}
          {view === "checkout" && hasReview && (
            <button
              ref={backRef}
              type="button"
              className="ProductStoreCartDialog__back"
              onClick={showReview}
            >
              <Icon icon="arrow-left-basic" />
              Back to order
            </button>
          )}
        </div>
        <button
          ref={closeRef}
          type="button"
          className="ProductStoreCartDialog__close"
          aria-label="Close"
          onClick={onClose}
        >
          <Icon icon="close-icon" />
        </button>
      </div>

      {view === "review" && review ? (
        review(() => setView("checkout"))
      ) : (
        <div className="ProductStoreCartDialog__body">
          {/* Stays mounted after payment: Chargebee shows its thank-you page
              in place. Closing it then closes the dialog. */}
          <ProductStoreEmbeddedCheckout
            loadHostedPage={createCheckout}
            onSuccess={() => {
              setView("complete");
              onComplete?.();
            }}
            onBack={view === "checkout" && hasReview ? showReview : onClose}
            backLabel={hasReview ? "Back to order" : "Close"}
          />
        </div>
      )}
    </Modal>
  );
};
